/**
 * Image Generator — persisted state.
 *
 * Every read/write of the arche's `localStorage` records lives here, so the state
 * hook holds reducer logic and this module holds the trust boundary. Storage is
 * mutable from devtools and left behind by older releases, so nothing that comes
 * out of it is trusted: each key has a `parse(raw: unknown)` that walks the
 * structure with per-field type guards and falls back to a safe default.
 *
 * ## Conventions
 *
 * - **Keys are scoped by `userId`** (see `../types/image-generator.types`), so a
 *   second Fias account on the same browser cannot read the first one's records.
 * - **Keys are versioned with a `:vN` suffix when their shape changes
 *   incompatibly.** Bumping orphans the old record rather than half-parsing it,
 *   and leaves a free rollback. A stale record costs nothing against a ~5 MB
 *   budget, so old keys are not swept. Only the panel-configs key is versioned
 *   today (`:v2`, the per-model-settings shape); the rest have never changed.
 * - **Reads and writes both fail silently.** Safari private mode rejects
 *   `setItem` with a quota error and SSR has no `window`; a throw inside a React
 *   effect would break the commit. Degrading to "this session is not persisted"
 *   is always better than breaking the page.
 * - **Every function takes an injectable `storage`**, defaulting to a per-call
 *   `safeLocalStorage()`. That is what makes these functions directly testable:
 *   `client/src/test/setup.ts` replaces `window.localStorage` with bare `vi.fn()`s
 *   that drop writes and return `undefined` on read.
 *
 * ## Deliberate non-goals
 *
 * - **No pruning or LRU cap on `modelSettings`.** "Nothing is thrown away" is the
 *   feature. The map is bounded in practice by the model catalogue (worst case
 *   ~5 panels x ~50 models, well under 100 KB). If that ever stops being true,
 *   the knob to add is an LRU eviction by `ImageModelSettings.updatedAt` — but
 *   measure first.
 * - **No cross-tab coordination.** Two tabs open on this arche are last-writer-
 *   wins, as they have always been. Per-model maps make a clobber larger but not
 *   different in kind.
 */

import {
  ENHANCEMENT_CATEGORIES,
  NUM_INSTANCES,
  createDefaultInstanceConfig,
  getEnhancementHistoryStorageKey,
  getInstanceConfigsStorageKey,
  getInstanceImagesStorageKey,
  getPanelLayoutStorageKey,
  getPresetsStorageKey,
  getTagDefinitionsStorageKey,
  isImageModelSettings,
  DEFAULT_MODEL_QUALITY,
  DEFAULT_MODEL_SIZE,
  DEFAULT_MODEL_STYLE,
  type EnhancementKey,
  type GeneratedImage,
  type ImageInstanceConfig,
  type ImageModelSettings,
  type ImagePreset,
  type ProviderParams,
  type ReferenceImage,
  type TagDefinition,
} from '../types/image-generator.types';
import { getModesStorageKey, type ImageMode } from '../types/image-generator-modes';

// ============================================================================
// Safe storage
// ============================================================================

/** The slice of the `Storage` API this module needs. */
export type KeyValueStorage = Pick<Storage, 'getItem' | 'setItem'> &
  Partial<Pick<Storage, 'removeItem'>>;

const NOOP_STORAGE: KeyValueStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

/**
 * Resolves `window.localStorage` per call rather than caching it at module
 * scope, so a test that swaps the global still sees the swap, and an origin that
 * throws on property access degrades to a no-op instead of failing at import.
 */
function safeLocalStorage(): KeyValueStorage {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return NOOP_STORAGE;
    return window.localStorage;
  } catch {
    return NOOP_STORAGE;
  }
}

/**
 * Reads and JSON-parses `key`, returning `undefined` when there is nothing
 * usable there. Note `raw == null`: the test-suite's `localStorage` stub returns
 * `undefined` rather than `null`, and `JSON.parse(undefined)` only "works"
 * because it throws into the catch — which is not something to rely on.
 */
function readJson(key: string, storage: KeyValueStorage): unknown {
  try {
    const raw = storage.getItem(key);
    if (raw == null || raw === '') return undefined;
    return JSON.parse(raw) as unknown;
  } catch {
    return undefined;
  }
}

/** Deletes `key`. Silently gives up when storage is unavailable. */
function removeKey(key: string, storage: KeyValueStorage): void {
  try {
    storage.removeItem?.(key);
  } catch {
    // Same reasoning as writeJson: never throw out of a storage operation.
  }
}

/** Writes `value` as JSON. Silently gives up when storage is full or unavailable. */
function writeJson(key: string, value: unknown, storage: KeyValueStorage): void {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota exceeded, private mode, or no storage at all. Losing persistence is
    // acceptable; throwing inside a React effect is not.
  }
}

// ============================================================================
// Shared parse helpers
// ============================================================================

/**
 * Keys that must never be accepted from parsed JSON when the object is used as a
 * lookup map. `JSON.parse` creates them as own properties rather than polluting
 * `Object.prototype`, but `map['constructor']` would still return a truthy object
 * that passes a `typeof === 'object'` check and then has `undefined` everywhere.
 */
const UNSAFE_MAP_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isSafeMapKey(key: string): boolean {
  return key !== '' && !UNSAFE_MAP_KEYS.has(key);
}

/**
 * Keeps only primitive param values. This both rejects nonsense from storage and
 * makes the shallow spread in `cloneModelSettings` a genuine deep copy — which is
 * what stops two panels seeded from each other sharing mutable state.
 */
function parseProviderParams(raw: unknown): ProviderParams {
  if (!isPlainObject(raw)) return {};
  const params: ProviderParams = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!isSafeMapKey(key)) continue;
    const type = typeof value;
    if (type === 'string' || type === 'number' || type === 'boolean') params[key] = value;
  }
  return params;
}

function parseModelSettings(raw: unknown): ImageModelSettings | null {
  if (!isPlainObject(raw)) return null;
  const candidate: ImageModelSettings = {
    size: typeof raw.size === 'string' ? raw.size : '',
    quality: typeof raw.quality === 'string' ? raw.quality : '',
    style: typeof raw.style === 'string' ? raw.style : '',
    providerParams: parseProviderParams(raw.providerParams),
    updatedAt:
      typeof raw.updatedAt === 'number' && Number.isFinite(raw.updatedAt) ? raw.updatedAt : 0,
  };
  if (!candidate.size || !candidate.quality || !candidate.style) return null;
  return isImageModelSettings(candidate) ? candidate : null;
}

function parseModelSettingsMap(raw: unknown): Record<string, ImageModelSettings> {
  if (!isPlainObject(raw)) return {};
  const entries: [string, ImageModelSettings][] = [];
  for (const [modelName, value] of Object.entries(raw)) {
    if (!isSafeMapKey(modelName)) continue;
    const settings = parseModelSettings(value);
    if (settings) entries.push([modelName, settings]);
  }
  return Object.fromEntries(entries);
}

function parseEnhancements(raw: unknown): Record<EnhancementKey, string> {
  const source = isPlainObject(raw) ? raw : {};
  return ENHANCEMENT_CATEGORIES.reduce(
    (acc, category) => {
      acc[category.key] =
        typeof source[category.key] === 'string' ? (source[category.key] as string) : '';
      return acc;
    },
    {} as Record<EnhancementKey, string>,
  );
}

function parseReferenceImage(raw: unknown): ReferenceImage | null {
  if (!isPlainObject(raw)) return null;
  if (typeof raw.thumbnailUrl !== 'string') return null;
  if (raw.source !== 'generated' && raw.source !== 'uploaded' && raw.source !== 'file-system') {
    return null;
  }
  const reference: ReferenceImage = {
    thumbnailUrl: raw.thumbnailUrl,
    source: raw.source,
  };
  if (typeof raw.imageRef === 'string') reference.imageRef = raw.imageRef;
  if (typeof raw.fileId === 'string') reference.fileId = raw.fileId;
  if (typeof raw.strength === 'number' && Number.isFinite(raw.strength)) {
    reference.strength = raw.strength;
  }
  return reference;
}

// ============================================================================
// Panel configs  (key: fias-image-instance-configs-<userId>:v2)
// ============================================================================

/**
 * Parses one stored panel. Returns null when the record is missing the fields
 * that make a panel a panel — including `modelSettings`, which is what makes a
 * pre-`:v2` record unusable rather than silently half-adopted.
 */
function parseInstanceConfig(raw: unknown, instanceId: number): ImageInstanceConfig | null {
  if (!isPlainObject(raw)) return null;
  if (typeof raw.modelName !== 'string' || raw.modelName === '') return null;
  if (typeof raw.provider !== 'string' || raw.provider === '') return null;
  if (!isPlainObject(raw.modelSettings)) return null;

  const modelSettings = parseModelSettingsMap(raw.modelSettings);

  return {
    instanceId,
    presetName: typeof raw.presetName === 'string' ? raw.presetName : 'Default Preset',
    originalPresetId: typeof raw.originalPresetId === 'string' ? raw.originalPresetId : null,
    includeModelInPreset: raw.includeModelInPreset === true,
    modelName: raw.modelName,
    provider: raw.provider,
    enhancements: parseEnhancements(raw.enhancements),
    referenceImage: parseReferenceImage(raw.referenceImage),
    modelSettings,
  };
}

/**
 * Loads all `NUM_INSTANCES` panels.
 *
 * Validation is **per panel**: one corrupt record resets only its own slot,
 * rather than discarding every panel the way a whole-array guard would.
 */
/**
 * The pre-per-model key. Its contents are deliberately never read — the shape is
 * incompatible and closed-beta panels are not migrated — but the record is
 * removed on first load so it does not sit in storage forever.
 */
function getLegacyInstanceConfigsStorageKey(userId: string): string {
  return `fias-image-instance-configs-${userId}`;
}

export function loadInstanceConfigs(
  userId: string,
  storage: KeyValueStorage = safeLocalStorage(),
): ImageInstanceConfig[] {
  removeKey(getLegacyInstanceConfigsStorageKey(userId), storage);
  const raw = readJson(getInstanceConfigsStorageKey(userId), storage);
  const saved = Array.isArray(raw) ? raw : [];

  return Array.from({ length: NUM_INSTANCES }, (_, instanceId) => {
    const record = saved.find((entry) => isPlainObject(entry) && entry.instanceId === instanceId);
    return parseInstanceConfig(record, instanceId) ?? createDefaultInstanceConfig(instanceId);
  });
}

export function saveInstanceConfigs(
  userId: string,
  configs: ImageInstanceConfig[],
  storage: KeyValueStorage = safeLocalStorage(),
): void {
  writeJson(getInstanceConfigsStorageKey(userId), configs, storage);
}

// ============================================================================
// Enhancement history  (key: fias-image-enhancement-history-<userId>)
// ============================================================================

export function loadEnhancementHistory(
  userId: string,
  storage: KeyValueStorage = safeLocalStorage(),
): Record<EnhancementKey, string[]> {
  const raw = readJson(getEnhancementHistoryStorageKey(userId), storage);
  const source = isPlainObject(raw) ? raw : {};
  // Rebuilt off ENHANCEMENT_CATEGORIES so a newly added category appears as [].
  return ENHANCEMENT_CATEGORIES.reduce(
    (acc, category) => {
      const stored = source[category.key];
      acc[category.key] = Array.isArray(stored)
        ? stored.filter((item): item is string => typeof item === 'string')
        : [];
      return acc;
    },
    {} as Record<EnhancementKey, string[]>,
  );
}

export function saveEnhancementHistory(
  userId: string,
  history: Record<EnhancementKey, string[]>,
  storage: KeyValueStorage = safeLocalStorage(),
): void {
  writeJson(getEnhancementHistoryStorageKey(userId), history, storage);
}

// ============================================================================
// Presets  (key: fias-image-presets-<userId>)
// ============================================================================

/** Presets stay FLAT — a preset is a cross-model recipe, not a per-model map. */
function parsePreset(raw: unknown): ImagePreset | null {
  if (!isPlainObject(raw)) return null;
  if (typeof raw.id !== 'string' || typeof raw.name !== 'string') return null;
  if (typeof raw.size !== 'string') return null;

  return {
    id: raw.id,
    name: raw.name,
    size: raw.size,
    // Same defaults a fresh model entry starts from — '' here would make the
    // preset permanently unmatchable, so it would always read as "(Unsaved)".
    quality: typeof raw.quality === 'string' && raw.quality ? raw.quality : DEFAULT_MODEL_QUALITY,
    style: typeof raw.style === 'string' && raw.style ? raw.style : DEFAULT_MODEL_STYLE,
    enhancements: parseEnhancements(raw.enhancements),
    providerParams: parseProviderParams(raw.providerParams),
    ...(typeof raw.modelName === 'string' ? { modelName: raw.modelName } : {}),
    ...(typeof raw.provider === 'string' ? { provider: raw.provider } : {}),
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : new Date(0).toISOString(),
    tags: Array.isArray(raw.tags) ? raw.tags.filter((t): t is string => typeof t === 'string') : [],
    categoryId: typeof raw.categoryId === 'string' ? raw.categoryId : null,
  };
}

export function loadPresets(
  userId: string,
  storage: KeyValueStorage = safeLocalStorage(),
): ImagePreset[] {
  const raw = readJson(getPresetsStorageKey(userId), storage);
  if (!Array.isArray(raw)) return [];
  return raw.map(parsePreset).filter((preset): preset is ImagePreset => preset !== null);
}

export function savePresets(
  userId: string,
  presets: ImagePreset[],
  storage: KeyValueStorage = safeLocalStorage(),
): void {
  writeJson(getPresetsStorageKey(userId), presets, storage);
}

// ============================================================================
// Tag definitions  (key: fias-image-tag-definitions-<userId>)
// ============================================================================

export function loadTagDefinitions(
  userId: string,
  storage: KeyValueStorage = safeLocalStorage(),
): TagDefinition[] {
  const raw = readJson(getTagDefinitionsStorageKey(userId), storage);
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (tag): tag is TagDefinition =>
        isPlainObject(tag) && typeof tag.id === 'string' && typeof tag.name === 'string',
    )
    .map((tag) => ({ id: tag.id, name: tag.name }));
}

export function saveTagDefinitions(
  userId: string,
  tags: TagDefinition[],
  storage: KeyValueStorage = safeLocalStorage(),
): void {
  writeJson(getTagDefinitionsStorageKey(userId), tags, storage);
}

// ============================================================================
// Modes  (keys: fias-image-modes-<userId>, and -builtin-tags for tag overrides)
// ============================================================================

function getBuiltInModeTagsStorageKey(userId: string): string {
  return `${getModesStorageKey(userId)}-builtin-tags`;
}

/** User-created modes only — built-ins are resolved from the entity catalogue. */
export function loadUserModes(
  userId: string,
  storage: KeyValueStorage = safeLocalStorage(),
): ImageMode[] {
  const raw = readJson(getModesStorageKey(userId), storage);
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (mode): mode is ImageMode =>
      isPlainObject(mode) &&
      typeof mode.id === 'string' &&
      typeof mode.name === 'string' &&
      Array.isArray(mode.panelConfigs) &&
      mode.panelConfigs.length >= 3,
  );
}

export function saveUserModes(
  userId: string,
  modes: ImageMode[],
  storage: KeyValueStorage = safeLocalStorage(),
): void {
  writeJson(getModesStorageKey(userId), modes, storage);
}

/** Per-user tag overrides for the built-in modes, keyed by mode id. */
export function loadBuiltInModeTags(
  userId: string,
  storage: KeyValueStorage = safeLocalStorage(),
): Record<string, string[]> {
  const raw = readJson(getBuiltInModeTagsStorageKey(userId), storage);
  if (!isPlainObject(raw)) return {};
  const entries: [string, string[]][] = [];
  for (const [modeId, value] of Object.entries(raw)) {
    if (!isSafeMapKey(modeId) || !Array.isArray(value)) continue;
    entries.push([modeId, value.filter((tag): tag is string => typeof tag === 'string')]);
  }
  return Object.fromEntries(entries);
}

export function saveBuiltInModeTags(
  userId: string,
  tagsByModeId: Record<string, string[]>,
  storage: KeyValueStorage = safeLocalStorage(),
): void {
  writeJson(getBuiltInModeTagsStorageKey(userId), tagsByModeId, storage);
}

// ============================================================================
// Session images  (key: fias-image-instance-images-<userId>)
// ============================================================================

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function optionalFiniteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/**
 * `imageUrl` is a presigned URL that expires, so it is stripped on save and
 * re-fetched on mount. `generatedAt` round-trips through JSON as a string and is
 * revived here, so the in-memory value always matches its declared `Date` type.
 *
 * Fields are rebuilt one at a time rather than spread: this record is rendered
 * straight into the gallery and the image-info dialog, so a stored `size` of
 * `{}` would reach `getAspectRatioClass` and a stored `prompt` of `null` would
 * reach `.substring`.
 */
function parseGeneratedImage(raw: unknown): GeneratedImage | null {
  if (!isPlainObject(raw)) return null;
  if (typeof raw.requestId !== 'string' || typeof raw.imageRef !== 'string') return null;

  const generatedAt =
    typeof raw.generatedAt === 'string' || typeof raw.generatedAt === 'number'
      ? new Date(raw.generatedAt)
      : new Date(0);

  return {
    requestId: raw.requestId,
    imageRef: raw.imageRef,
    // Stripped on save; refreshed from the API after mount.
    imageUrl: optionalString(raw.imageUrl) ?? '',
    prompt: optionalString(raw.prompt) ?? '',
    revisedPrompt: optionalString(raw.revisedPrompt),
    provider: optionalString(raw.provider) ?? '',
    model: optionalString(raw.model) ?? '',
    size: optionalString(raw.size) ?? DEFAULT_MODEL_SIZE,
    quality: optionalString(raw.quality) ?? DEFAULT_MODEL_QUALITY,
    style: optionalString(raw.style) ?? DEFAULT_MODEL_STYLE,
    enhancements: parseEnhancements(raw.enhancements),
    generatedAt: Number.isNaN(generatedAt.getTime()) ? new Date(0) : generatedAt,
    instanceId: optionalFiniteNumber(raw.instanceId),
    usedReferenceImage:
      typeof raw.usedReferenceImage === 'boolean' ? raw.usedReferenceImage : undefined,
    creativityPercent: optionalFiniteNumber(raw.creativityPercent),
    costCredits: optionalFiniteNumber(raw.costCredits),
  };
}

export function loadInstanceImages(
  userId: string,
  storage: KeyValueStorage = safeLocalStorage(),
): GeneratedImage[][] {
  const raw = readJson(getInstanceImagesStorageKey(userId), storage);
  const saved = Array.isArray(raw) ? raw : [];
  return Array.from({ length: NUM_INSTANCES }, (_, instanceId) => {
    const images = saved[instanceId];
    if (!Array.isArray(images)) return [];
    return images
      .map(parseGeneratedImage)
      .filter((image): image is GeneratedImage => image !== null);
  });
}

export function saveInstanceImages(
  userId: string,
  instanceImages: GeneratedImage[][],
  storage: KeyValueStorage = safeLocalStorage(),
): void {
  const stripped = instanceImages.map((images) =>
    images.map(({ imageUrl: _imageUrl, ...rest }) => rest),
  );
  writeJson(getInstanceImagesStorageKey(userId), stripped, storage);
}

// ============================================================================
// Panel layout  (key: fias-image-panel-layout-<userId>)
// ============================================================================

export interface PanelLayout {
  enabledInstances: boolean[];
  minimizedInstances: boolean[];
}

/** First three panels on, none minimized. */
export function createDefaultPanelLayout(): PanelLayout {
  return {
    enabledInstances: Array.from({ length: NUM_INSTANCES }, (_, i) => i < 3),
    minimizedInstances: Array.from({ length: NUM_INSTANCES }, () => false),
  };
}

function parseBooleanArray(raw: unknown): boolean[] | null {
  if (!Array.isArray(raw) || raw.length !== NUM_INSTANCES) return null;
  if (!raw.every((value) => typeof value === 'boolean')) return null;
  return raw as boolean[];
}

export function loadPanelLayout(
  userId: string,
  storage: KeyValueStorage = safeLocalStorage(),
): PanelLayout {
  const raw = readJson(getPanelLayoutStorageKey(userId), storage);
  if (!isPlainObject(raw)) return createDefaultPanelLayout();
  const enabledInstances = parseBooleanArray(raw.enabledInstances);
  const minimizedInstances = parseBooleanArray(raw.minimizedInstances);
  if (!enabledInstances || !minimizedInstances) return createDefaultPanelLayout();
  // A layout with nothing enabled would strand the user with no panels at all.
  if (!enabledInstances.some(Boolean)) return createDefaultPanelLayout();
  return { enabledInstances, minimizedInstances };
}

export function savePanelLayout(
  userId: string,
  layout: PanelLayout,
  storage: KeyValueStorage = safeLocalStorage(),
): void {
  writeJson(getPanelLayoutStorageKey(userId), layout, storage);
}
